import React, {useCallback, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricGreetingCard/api/event';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricGreetingCard/navigation';
import {ConfirmLeaveModalContainer} from '~/CreateFabricGreetingCard/navigation/ConfirmLeaveModal';
import {numSteps} from '~/CreateFabricGreetingCard/navigation/createSteps';
import {UPDATE_EVENT_DETAILS} from '~/CreateFabricGreetingCard/reducers/details/constants';
import {getEventId} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {
  selectPristine,
  selectPristineUyo,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {InjectableMegaNav} from '~library/organisms/InjectableMegaNav';
import {SimpleNav} from '~library/organisms/SimpleNav';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {ProgressBar} from './ProgressBar';
import {isFabricWebview} from '~/common/utils/webview';
import {isInAppMode} from '~/common/checkout/utils/app';

import './NavHeader.sass';

export const NavHeader = ({isPremium}) => {
  const inAppMode = isInAppMode();
  const currentRoute = useCurrentRoute();
  const store = useStore();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const showSteps = currentRoute.meta.countSteps;
  const user = useSelector((state) => state?.user);

  const pristine = useSelector(selectPristine);
  const pristineUyo = useSelector(selectPristineUyo);
  const hasBeenSaved = !!useSelector(getEventId);

  const totalSteps =
    numSteps - useSelector((state) => (!state.details.isPremium && isFabricWebview() ? 1 : 0));
  const simpleNavObj = {
    prev: {
      onClick: async () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        await navigateToRoute('back', store);
      },
      text: 'Back',
      qaId: 'upsell-back',
      buttonVariant: 'secondary',
    },
    next: {
      onClick: async () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        await navigateToRoute('next', store);
      },
      text: 'Skip',
      qaId: 'upsell-next',
      buttonVariant: 'secondary',
    },
  };

  const onClickCloseBtn = useCallback(() => {
    if (pristine && pristineUyo) {
      return navigateToRoute('exit', store, true);
    }

    return setShowLeaveModal(true);
  }, [pristine, pristineUyo, store]);

  const onPreviewClick = useCallback(async () => {
    const savePromise = store.dispatch(saveEventThunk(true));
    await navigateToRoute('preview', store);
    await savePromise;
  }, [store]);

  const onBackClick = useCallback(() => navigateToRoute('back', store), [store]);

  const onLeaveClick = useCallback(async () => {
    setShowLeaveModal(false);
    await navigateToRoute('exit', store, !hasBeenSaved);
  }, [hasBeenSaved, store]);

  if (currentRoute.showMegaNav) {
    return <InjectableMegaNav user={user} />;
  }
  if (currentRoute.showSimpleNav) {
    return <SimpleNav prev={simpleNavObj.prev} next={simpleNavObj.next} />;
  }

  return (
    <>
      <div className="fabric_header__wrapper">
        <header className={`fabric_header${currentRoute.id > totalSteps ? ' frp' : ''}`}>
          <nav className={`fabric_header__nav${currentRoute.id > totalSteps ? ' frp' : ''}`}>
            {!inAppMode && (
              <Button
                id="continueBtn"
                data-qa-id="exit-create"
                className="fabric_header__back"
                variant="link"
                size="small"
                onClick={onClickCloseBtn}
              >
                <CloseLineIcon ratio={0.75} />
              </Button>
            )}
            <Typography variant="paragraph1" color="pebble" classNames="fabric-header__steps">
              {isPremium && <DiamondIcon ratio={16 / 14.0} className="fabric_header__is-premium" />}
              {isPremium && showSteps && ' '}
              {showSteps ? `${currentRoute.id} of ${totalSteps}:` : ''}
            </Typography>
            <Typography variant="paragraph1" classNames="fabric-header__step">
              &nbsp;
              {currentRoute.displayName || ''}
            </Typography>
            {currentRoute.linkPreview && (
              <Button
                id="continueBtn"
                data-qa-id="continueBtn"
                className="fabric_header__continue"
                variant="link"
                size="large"
                onClick={onPreviewClick}
              >
                Preview
              </Button>
            )}
            {currentRoute.linkBack && (
              <Button
                id="continueBtn"
                data-qa-id="continueBtn"
                className="fabric_header__continue"
                variant="link"
                size="large"
                onClick={onBackClick}
              >
                Back
              </Button>
            )}
          </nav>
        </header>
        <ProgressBar />
      </div>
      {showLeaveModal && (
        <ConfirmLeaveModalContainer
          isFirstStep={!hasBeenSaved}
          isEditingFlow={currentRoute.meta.editing}
          leave={onLeaveClick}
          cancelLeave={() => setShowLeaveModal(false)}
        />
      )}
    </>
  );
};
